import React, { useState, useRef, useEffect } from 'react';
import { AlertCircle, CheckCircle, Upload, Download, X } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';

const LANGUAGE_NAMES = {
  tr: 'Turkish', en: 'English', de: 'German', fr: 'French', es: 'Spanish',
  it: 'Italian', pt: 'Portuguese', ja: 'Japanese', zh: 'Chinese', ar: 'Arabic',
};

const SAMPLE_NAMES = {
  tr: { cat: 'Başlangıçlar', item: 'Pasta Karbonara', desc: 'Klasik İtalyan pastası' },
  en: { cat: 'Appetizers', item: 'Pasta Carbonara', desc: 'Classic Italian pasta' },
  de: { cat: 'Vorspeisen', item: 'Pasta Carbonara', desc: 'Klassische italienische Pasta' },
  fr: { cat: 'Entrées', item: 'Pâtes Carbonara', desc: 'Pâtes italiennes classiques' },
  es: { cat: 'Entrantes', item: 'Pasta Carbonara', desc: 'Pasta italiana clásica' },
  it: { cat: 'Antipasti', item: 'Pasta alla Carbonara', desc: 'Pasta italiana classica' },
  pt: { cat: 'Entradas', item: 'Massa Carbonara', desc: 'Massa italiana clássica' },
  ja: { cat: '前菜', item: 'カルボナーラ', desc: 'クラシックイタリアンパスタ' },
  zh: { cat: '开胃菜', item: '卡布奇诺意面', desc: '经典意大利面' },
  ar: { cat: 'مقبلات', item: 'باستا كاربونارا', desc: 'باستا إيطالية كلاسيكية' },
};

const ImportFileDialog = ({ isOpen, onClose, importType, onSuccess, supportedLanguages }) => {
  const langs = supportedLanguages && supportedLanguages.length > 0 ? supportedLanguages : ['tr'];
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setImportResult(null);
      setIsDragging(false);
    }
  }, [isOpen]);

  const importTypeConfig = {
    categories: {
      title: 'Import Categories',
      description: 'Upload a CSV or Excel file with category data',
      endpoint: '/api/import/categories',
      templateColumns: [
        'external_id',
        ...langs.map(l => `name_${l}`),
        'vat_percentage', 'order'
      ],
      templateSampleRow: [
        'CAT-001',
        ...langs.map(l => (SAMPLE_NAMES[l] || SAMPLE_NAMES.en).cat),
        '18', '0'
      ],
      templateFileName: 'categories_template.csv',
    },
    menu_items: {
      title: 'Import Menu Items',
      description: 'Upload a CSV or Excel file with menu item data',
      endpoint: '/api/import/menu-items',
      templateColumns: [
        'external_id',
        ...langs.map(l => `name_${l}`),
        ...langs.map(l => `description_${l}`),
        'category_id', 'base_price', 'image_url', 'available'
      ],
      templateSampleRow: [
        'ITEM-001',
        ...langs.map(l => (SAMPLE_NAMES[l] || SAMPLE_NAMES.en).item),
        ...langs.map(l => (SAMPLE_NAMES[l] || SAMPLE_NAMES.en).desc),
        'cat-123', '45.99', 'https://example.com/image.jpg', 'true'
      ],
      templateFileName: 'menu_items_template.csv',
    },
  };

  const config = importTypeConfig[importType];

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      const droppedFile = droppedFiles[0];
      validateAndSetFile(droppedFile);
    }
  };

  const validateAndSetFile = (selectedFile) => {
    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    const allowedExtensions = ['.csv', '.xls', '.xlsx'];

    const hasValidExtension = allowedExtensions.some((ext) =>
      selectedFile.name.toLowerCase().endsWith(ext)
    );

    if (!hasValidExtension) {
      toast({
        title: 'Invalid File Type',
        description: 'Please upload a CSV or Excel file (.csv, .xls, .xlsx)',
        variant: 'destructive',
      });
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'File size must be less than 10 MB',
        variant: 'destructive',
      });
      return;
    }

    setFile(selectedFile);
    setImportResult(null);
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const downloadTemplate = () => {
    const csv = [
      config.templateColumns.join(','),
      config.templateSampleRow.join(','),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', config.templateFileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImport = async () => {
    if (!file) {
      toast({
        title: 'No File Selected',
        description: 'Please select a file to import',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(config.endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      setImportResult(response.data);
      const summary = response.data.added_count !== undefined
        ? `Added: ${response.data.added_count}, Updated: ${response.data.updated_count}, Errors: ${response.data.error_count}`
        : `${response.data.imported_count} items imported successfully`;
      toast({
        title: 'Import Completed',
        description: summary,
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Import failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Import failed';
      setImportResult({
        message: errorMsg,
        imported_count: 0,
        is_error: true,
      });
      toast({
        title: 'Import Failed',
        description: errorMsg,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setImportResult(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Import Result */}
          {importResult && (
            <div
              className={`p-4 rounded-lg border ${
                importResult.is_error
                  ? 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800'
                  : 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800'
              }`}
            >
              <div className="flex gap-2">
                {importResult.is_error ? (
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p
                    className={`font-medium ${
                      importResult.is_error ? 'text-red-900 dark:text-red-200' : 'text-green-900 dark:text-green-200'
                    }`}
                  >
                    {importResult.message}
                  </p>
                  <div className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                    {importResult.added_count !== undefined && (
                      <p>Added: <strong>{importResult.added_count}</strong></p>
                    )}
                    {importResult.updated_count !== undefined && (
                      <p>Updated: <strong>{importResult.updated_count}</strong></p>
                    )}
                    {importResult.error_count !== undefined && (
                      <p className={importResult.error_count > 0 ? 'text-red-700 dark:text-red-400 font-semibold' : ''}>Errors: <strong>{importResult.error_count}</strong></p>
                    )}
                  </div>
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-y-auto">
                      <p className="text-sm font-medium mb-1">Errors:</p>
                      <ul className="text-sm space-y-1">
                        {importResult.errors.map((err, idx) => (
                          <li key={idx} className="text-red-700 dark:text-red-400">
                            • {err}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* File Upload Area */}
          {!importResult && (
            <>
              <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragging
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                    : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50'
                }`}
              >
                <Upload
                  className={`w-12 h-12 mx-auto mb-3 ${
                    isDragging ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'
                  }`}
                />
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  Drag and drop your file here
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  or click to select a file
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                  Supported: CSV, XLS, XLSX (max 10 MB)
                </p>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xls,.xlsx"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-4"
                >
                  Select File
                </Button>
              </div>

              {/* Selected File */}
              {file && (
                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Upload className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <div>
                      <p className="font-medium text-sm text-gray-900 dark:text-gray-100">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {(file.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setFile(null)}
                    className="p-1 hover:bg-blue-100 rounded"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              )}
            </>
          )}

          {/* Template Download */}
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200 mb-2">
              Need a template?
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadTemplate}
              className="w-full gap-2"
            >
              <Download className="w-4 h-4" />
              Download Template
            </Button>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            {importResult ? (
              <Button onClick={handleClose}>
                Close
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={!file || isLoading}
                  className="gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Import
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportFileDialog;
